// A '.tsx' file enables JSX support in the TypeScript compiler, 
// for more information see the following page on the TypeScript wiki:
// https://github.com/Microsoft/TypeScript/wiki/JSX

import { DialogComponent } from '@syncfusion/ej2-react-popups';
import React from 'react';
import * as Inovx from '@shared';
import FacilityActions from '../actions/FacilityActions';
import FacilityStore from '../stores/FacilityStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import ComponentHelpers from '../helpers/ComponentHelpers';
import OntologyActions from '../actions/OntologyActions';
import ViewerStore from '../stores/ViewerStore';
import KnowledgeLibraryHierarchy from './KnowledgeLibraryHierarchy';
import KnowledgeBookForm from './knowledgeLibrary/KnowledgeBookForm';
import KnowledgeBookAssignClassificationTagsForm from './knowledgeLibrary/KnowledgeBookAssignClassificationTagsForm';
import viewerActions from '../actions/ViewerActions';
import KnowledgeLibraryFilterBar from './knowledgeLibrary/KnowledgeLibraryFilterBar';
import KnowledgeLibraryCards from './knowledgeLibrary/KnowledgeLibraryCards';
import KnowledgeViewsDrawer from './knowledgeLibrary/KnowledgeViewsDrawer';
import OperationProgressDialog from './OperationProgressDialog';
import KnowledgeViewServices from '../services/KnowledgeViewServices';
import { KnowledgeLibraryBreadcrumb, buildKnowledgeLibraryBrowseModel, filterKnowledgeBooksBySearchText } from './knowledgeLibrary/knowledgeLibraryBrowseModel';
import KnowledgeLibraryBulkSelectionToolbar from './knowledgeLibrary/KnowledgeLibraryBulkSelectionToolbar';
import { ClassificationFilterSortDirection } from './knowledgeLibrary/KnowledgeLibraryClassificationFilter';
import NewKnowledgeBookDialog from './knowledgeLibrary/NewKnowledgeBookDialog';
import { NewKnowledgeBookCreationResult } from './knowledgeLibrary/knowledgeBookCreationModel';

const { useState, useEffect, useRef, useCallback, useMemo } = React;

var SystemActions = Inovx.Shared.System.Actions;

type KnowledgeLibraryViewMode = 'card' | 'list';
const EMPTY_ARRAY: never[] = [];
const LEGACY_NOT_CLASSIFIED_CARD_ID = 'none';

interface KnowledgeLibraryState {
    knowledgeBooks: IKnowledgeBook[];
    favoriteKnowledgeBookIds: string[];
    selectedFacility: IFacilityDetail | null;
    isModalOpen: boolean;
    cardUrlState: KnowledgeLibraryCardUrlState;
    isSidebarOpen: boolean;
}

function getStoreState(): KnowledgeLibraryState {
    return {
        knowledgeBooks: KnowledgeBookStore.getAll() || [],
        favoriteKnowledgeBookIds: KnowledgeBookStore.getFavoriteKnowledgeBookIds() || [],
        selectedFacility: FacilityStore.getSelectedFacility(),
        isModalOpen: KnowledgeBookStore.getKBModalVisible(),
        cardUrlState: FacilityStore.getKnowledgeLibraryCardUrlState(),
        isSidebarOpen: FacilityStore.getSidebarOpen()
    };
}

const KnowledgeLibrary: React.FC = () => {
    const initialCardUrlState = FacilityStore.getKnowledgeLibraryCardUrlState();
    const [state, setState] = useState<KnowledgeLibraryState>(getStoreState);
    const [viewMode, setViewMode] = useState<KnowledgeLibraryViewMode>(initialCardUrlState.isCardView ? 'card' : 'list');
    const [selectedOntologyId, setSelectedOntologyId] = useState<string>();
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [classificationFilterTagIds, setClassificationFilterTagIds] = useState<Set<string> | null>(null);
    const [classificationFilterSortDirection, setClassificationFilterSortDirection] = useState<ClassificationFilterSortDirection>('asc');
    const [selectedClassificationTagId, setSelectedClassificationTagId] = useState<string | undefined>(initialCardUrlState.classificationTagId === LEGACY_NOT_CLASSIFIED_CARD_ID ? undefined : initialCardUrlState.classificationTagId);
    const [detailKnowledgeBook, setDetailKnowledgeBook] = useState<IKnowledgeBook | null>(null);
    const [revealedKnowledgeBookId, setRevealedKnowledgeBookId] = useState<string | null>(initialCardUrlState.classificationTagId === LEGACY_NOT_CLASSIFIED_CARD_ID ? null : (initialCardUrlState.knowledgeBookId || null));
    const [detailKnowledgeViews, setDetailKnowledgeViews] = useState<IKnowledgeView[]>([]);
    const [isLoadingDetailKnowledgeViews, setIsLoadingDetailKnowledgeViews] = useState(false);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);
    const [isNewKnowledgeBookDialogOpen, setIsNewKnowledgeBookDialogOpen] = useState(false);
    const [selectedKnowledgeBookIdSet, setSelectedKnowledgeBookIdSet] = useState<Set<string>>(() => new Set());
    const mountedRef = useRef(true);
    const knowledgeViewsContainerRef = useRef<HTMLDivElement>(null);
    const detailKnowledgeBookRequestRef = useRef(0);
    const viewModeRef = useRef<KnowledgeLibraryViewMode>(viewMode);
    const currentClassificationTagIdRef = useRef<string | undefined>(initialCardUrlState.classificationTagId === LEGACY_NOT_CLASSIFIED_CARD_ID ? undefined : initialCardUrlState.classificationTagId);
    const canViewKnowledgeBookRef = useRef<(knowledgeBook: IKnowledgeBook) => boolean>(() => true);
    const canModifyKnowledgeBookRef = useRef<(knowledgeBook: IKnowledgeBook) => boolean>(() => true);
    const canManageKnowledgeBookRef = useRef<(knowledgeBook: IKnowledgeBook) => boolean>(() => true);

    useEffect(() => {
        viewModeRef.current = viewMode;
    }, [viewMode]);

    // Subscribe to stores
    useEffect(() => {
        const handleStoreChange = () => {
            if (mountedRef.current) {
                setState(getStoreState());
            }
        };

        KnowledgeBookStore.addChangeListener(handleStoreChange);
        FacilityStore.addChangeListener(handleStoreChange);
        ViewerStore.addChangeListener(handleStoreChange);
        KnowledgeBookActions.getFavoriteKnowledgeBookIds();
        FacilityActions.setKnowledgeLibraryCardUrlState({
            isCardView: initialCardUrlState.isCardView,
            classificationTagId: initialCardUrlState.classificationTagId,
            knowledgeBookId: initialCardUrlState.knowledgeBookId
        });

        return () => {
            mountedRef.current = false;
            KnowledgeBookStore.removeChangeListener(handleStoreChange);
            FacilityStore.removeChangeListener(handleStoreChange);
            ViewerStore.removeChangeListener(handleStoreChange);
        };
    }, []);

    const handleBeginEdit = useCallback((args: any) => {
        const knowledgeBook = (args?.rowData as IKnowledgeBook) || (args?.data?.[0] as IKnowledgeBook);

        if (args.requestType === 'delete') {
            if (knowledgeBook && !canManageKnowledgeBookRef.current(knowledgeBook)) {
                args.cancel = true;
                SystemActions.displayInfoMessage({ message: InovxLoader.t('facility:message.knowledgeBookNoManagePermission') });
                return;
            }

            args.cancel = true;
            handleDelete(args);
        } else if (args.requestType === 'beginEdit') {
            if (knowledgeBook && !canModifyKnowledgeBookRef.current(knowledgeBook)) {
                args.cancel = true;
                SystemActions.displayInfoMessage({ message: InovxLoader.t('facility:message.knowledgeBookNoModifyPermission') });
                return;
            }

            args.cancel = true;
            KnowledgeBookActions.beginEdit(args.rowData);
        }
    }, []);

    const handleDelete = useCallback(async (args: any) => {
        if (args.requestType === 'delete') {
            const messageObject = {
                message: InovxLoader.t('facility:message.confirmDeleteKB'),
                confirmCallback: async function () {
                    await KnowledgeBookActions.deleteKnowledgeBook(args.data[0]);
                }
            };
            SystemActions.displayConfirmationMessage(messageObject);
        }
    }, []);

    const openKnowledgeBookFromLibrary = useCallback(async (kb: IKnowledgeBook) => {
        const { selectedFacility } = state;

        if (!canViewKnowledgeBookRef.current(kb)) {
            SystemActions.displayInfoMessage({ message: InovxLoader.t('facility:message.knowledgeBookNoViewPermission') });
            return;
        }

        const kbData = await KnowledgeBookActions.openKnowledgeBook(kb);
        const kvData = kbData.KnowledgeViews;
        if (kvData.length > 0) {
            const kv = kvData[0];
            if (kv.Type === 2 && selectedFacility) { // 3D View
                await viewerActions.changeKnowledgeBookViewingMode('manageKV', false);
                const ontology = selectedFacility.OntologyParts?.find(
                    (part: IOntologyPart) => part.ID === kb.OntologyID
                );
                if (ontology) {
                    OntologyActions.selectOntology(ontology);
                }
            }

            await FacilityActions.openKnowledgeViewInViewer(kv);
            KnowledgeBookActions.pinUnpinKBPopup(true);
        }

        // Opening a book from Knowledge Library always starts in viewing mode.
        // This also intentionally replaces any remembered Edit View destination.
        FacilityActions.openKBbar();
    }, [state.selectedFacility]);

    const handleNewKnowledgeBookCreated = useCallback(async (result: NewKnowledgeBookCreationResult) => {
        if (result.initialViewError || !result.initialView) {
            SystemActions.displayWarningMessage({
                message: InovxLoader.t('facility:assetSetVisualization.knowledgeBookCreatedWithoutInitialView')
            });
            return;
        }

        try {
            await openKnowledgeBookFromLibrary(result.knowledgeBook);
        } catch (error) {
            console.error('The new Knowledge Book could not be opened.', error);
            SystemActions.displayWarningMessage({
                message: InovxLoader.t('facility:assetSetVisualization.knowledgeBookCreatedButNotOpened')
            });
        }
    }, [openKnowledgeBookFromLibrary]);

    const handleCommandClick = useCallback(async (args: any) => {
        if (args.commandColumn?.type === 'View') {
            await openKnowledgeBookFromLibrary(args.rowData as IKnowledgeBook);
        }
    }, [openKnowledgeBookFromLibrary]);

    
    const handleFavoriteClick = useCallback(async (kb: IKnowledgeBook) => {
        await KnowledgeBookActions.toggleFavoriteKnowledgeBook(kb);
    }, []);

    const handleAddToFavorites = useCallback(async (kbs: IKnowledgeBook[]) => {
        await KnowledgeBookActions.addFavoritesForKnowledgeBooks(kbs);
    }, []);

    const handleRemoveFromFavorites = useCallback(async (kbs: IKnowledgeBook[]) => {
        await KnowledgeBookActions.removeFavoritesForKnowledgeBooks(kbs);
    }, []);

    const handleToggleKnowledgeBookSelection = useCallback((knowledgeBookId: string) => {
        setSelectedKnowledgeBookIdSet(previous => {
            const next = new Set(previous);
            if (next.has(knowledgeBookId)) {
                next.delete(knowledgeBookId);
            } else {
                next.add(knowledgeBookId);
            }

            return next;
        });
    }, []);

    const clearKnowledgeBookSelection = useCallback(() => {
        setSelectedKnowledgeBookIdSet(new Set());
    }, []);

    const handleAssignTags = useCallback((kbs: IKnowledgeBook[]) => {
        KnowledgeBookActions.openAssignClassificationTagsForm(kbs);
    }, []);

    const handleDeleteKnowledgeBooks = useCallback((kbs: IKnowledgeBook[]) => {
        if (kbs.length === 0) return;

        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:knowledgeBook.confirmDeleteSelected'),
            confirmCallback: async function () {
                setOperationMessage(InovxLoader.t('facility:knowledgeBook.deletingSelected'));
                try {
                    const result = await KnowledgeBookActions.batchDeleteKnowledgeBooks(kbs);
                    const deletedKnowledgeBookIds = new Set(kbs.map(kb => kb.ID));
                    if (revealedKnowledgeBookId && deletedKnowledgeBookIds.has(revealedKnowledgeBookId)) {
                        detailKnowledgeBookRequestRef.current += 1;
                        setRevealedKnowledgeBookId(null);
                        setDetailKnowledgeBook(null);
                        setDetailKnowledgeViews(current => current.length === 0 ? current : []);
                        setIsLoadingDetailKnowledgeViews(false);
                        FacilityActions.setKnowledgeLibraryCardUrlState({
                            isCardView: true,
                            classificationTagId: currentClassificationTagIdRef.current,
                            knowledgeBookId: undefined
                        });
                    }

                    if (result?.HasPartialFailure) {
                        SystemActions.displayWarningMessage({
                            message: InovxLoader.t('facility:knowledgeBook.partialDeleteFailure')
                        });
                    }
                } finally {
                    setOperationMessage(null);
                }
            }
        });
    }, [revealedKnowledgeBookId]);

    const openKnowledgeViewFromLibrary = useCallback(async (kv: IKnowledgeView) => {
        const { selectedFacility } = state;

        let kb = detailKnowledgeBook;
        if (!kb || kb.ID !== kv.KnowledgeBookID) {
            kb = await KnowledgeBookActions.getKnowledgeBook(kv.KnowledgeBookID);
        }

        if (!kb) {
            return;
        }

        const kbData = await KnowledgeBookActions.openKnowledgeBook(kb);
        const targetKnowledgeView = kbData.KnowledgeViews?.find(item => item.ID === kv.ID) || kv;

        if (targetKnowledgeView.Type === 2 && selectedFacility) { // 3D View
            await viewerActions.changeKnowledgeBookViewingMode('manageKV', false);
            const ontology = selectedFacility.OntologyParts?.find(
                (part: IOntologyPart) => part.ID === kb.OntologyID
            );
            if (ontology) {
                OntologyActions.selectOntology(ontology);
            }
        }

        await FacilityActions.openKnowledgeViewInViewer(targetKnowledgeView);
        KnowledgeBookActions.pinUnpinKBPopup(true);
        FacilityActions.openKBbar();
    }, [detailKnowledgeBook, state.selectedFacility]);

    const handleRevealedKnowledgeBookChange = useCallback(async (kb: IKnowledgeBook | null) => {
        if (!kb) {
            if (!detailKnowledgeBook && detailKnowledgeViews.length === 0 && !isLoadingDetailKnowledgeViews) {
                return;
            }

            detailKnowledgeBookRequestRef.current += 1;
            setDetailKnowledgeBook(null);
            setDetailKnowledgeViews(current => current.length === 0 ? current : []);
            setIsLoadingDetailKnowledgeViews(false);
            return;
        }

        if (detailKnowledgeBook?.ID === kb.ID) {
            return;
        }

        const requestId = detailKnowledgeBookRequestRef.current + 1;
        detailKnowledgeBookRequestRef.current = requestId;
        setDetailKnowledgeBook(kb);
        setDetailKnowledgeViews(current => current.length === 0 ? current : []);
        setIsLoadingDetailKnowledgeViews(true);

        try {
            const knowledgeViews = await KnowledgeViewServices.getKnowledgeViews(kb.ID);
            if (mountedRef.current && detailKnowledgeBookRequestRef.current === requestId) {
                setDetailKnowledgeViews(knowledgeViews || []);
            }
        } finally {
            if (mountedRef.current && detailKnowledgeBookRequestRef.current === requestId) {
                setIsLoadingDetailKnowledgeViews(false);
            }
        }
    }, [detailKnowledgeBook, detailKnowledgeViews.length, isLoadingDetailKnowledgeViews]);

    const { knowledgeBooks, favoriteKnowledgeBookIds, isModalOpen, selectedFacility, cardUrlState } = state;

    const favoriteKnowledgeBookIdSet = useMemo(() => new Set(favoriteKnowledgeBookIds), [favoriteKnowledgeBookIds]);

    const taxonomyDetail = FacilityStore.getVisibleTaxonomyClassificationDetail();
    const classificationTags = taxonomyDetail?.ClassificationTags || EMPTY_ARRAY as IClassificationTag[];

    const classificationTagIdSet = useMemo(() => {
        return new Set(classificationTags.map(tag => tag.ID));
    }, [classificationTags]);

    const allowedViewTagIds = useMemo(() => {
        return new Set(classificationTags.map(tag => tag.ID));
    }, [classificationTags]);

    const effectiveClassificationFilterTagIds = useMemo(() => {
        if (!classificationFilterTagIds) {
            return allowedViewTagIds;
        }

        const filteredTagIds = new Set<string>();
        classificationFilterTagIds.forEach((tagId) => {
            if (allowedViewTagIds.has(tagId)) {
                filteredTagIds.add(tagId);
            }
        });

        return filteredTagIds;
    }, [allowedViewTagIds, classificationFilterTagIds]);

    const canViewKnowledgeBook = useCallback((knowledgeBook: IKnowledgeBook) => {
        return ComponentHelpers.hasViewKnowledgeBookPermission(knowledgeBook);
    }, [classificationTags]);

    const canModifyKnowledgeBook = useCallback((knowledgeBook: IKnowledgeBook) => {
        return ComponentHelpers.hasModifyKnowledgeBookPermission(knowledgeBook);
    }, [classificationTags]);

    const canManageKnowledgeBookByTags = useCallback((knowledgeBook: IKnowledgeBook) => {
        return ComponentHelpers.hasManageKnowledgeBookPermission(knowledgeBook);
    }, [classificationTags]);

    useEffect(() => {
        canViewKnowledgeBookRef.current = canViewKnowledgeBook;
        canModifyKnowledgeBookRef.current = canModifyKnowledgeBook;
        canManageKnowledgeBookRef.current = canManageKnowledgeBookByTags;
    }, [canManageKnowledgeBookByTags, canModifyKnowledgeBook, canViewKnowledgeBook]);

    const classificationFilterValues = useMemo(() => {
        return classificationTags
            .filter(tag => allowedViewTagIds.has(tag.ID))
            .map(tag => ({ id: tag.ID, name: tag.Name }))
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [allowedViewTagIds, classificationTags]);

    const facilityHierarchy = useMemo(() => {
        if (!selectedFacility?.OntologyParts?.length) {
            return null;
        }

        return FacilityStore.getFacilityHierarchy();
    }, [selectedFacility]);

    const ontologyHierarchy = useMemo(() => {
        if (!selectedFacility?.OntologyParts?.length) {
            return {} as IOntologyHierarchyMap;
        }

        return FacilityStore.getOntologyBoundsContext().ontologyHierarchy;
    }, [selectedFacility]);

    useEffect(() => {
        if (!selectedOntologyId) {
            return;
        }

        const ontologyExists = selectedFacility?.OntologyParts?.some(part => part.ID === selectedOntologyId);
        if (!ontologyExists) {
            setSelectedOntologyId(undefined);
        }
    }, [selectedFacility, selectedOntologyId]);

    useEffect(() => {
        if (classificationTags.length > 0 && selectedClassificationTagId && !classificationTagIdSet.has(selectedClassificationTagId)) {
            setSelectedClassificationTagId(undefined);
            currentClassificationTagIdRef.current = undefined;
            FacilityActions.setKnowledgeLibraryCardUrlState({ classificationTagId: undefined });
        }
    }, [classificationTagIdSet, classificationTags.length, selectedClassificationTagId]);

    useEffect(() => {
        setSelectedClassificationTagId(undefined);
        setClassificationFilterTagIds(null);
        currentClassificationTagIdRef.current = undefined;
    }, [selectedFacility?.ID, taxonomyDetail?.ID, taxonomyDetail?.VersionToken]);

    useEffect(() => {
        if (!cardUrlState.isCardView) {
            setViewMode('list');
            return;
        }

        setViewMode('card');
        const isLegacyNotClassifiedUrl = cardUrlState.classificationTagId === LEGACY_NOT_CLASSIFIED_CARD_ID;
        if (isLegacyNotClassifiedUrl) {
            setSelectedClassificationTagId(undefined);
            currentClassificationTagIdRef.current = undefined;
        } else if (cardUrlState.classificationTagId) {
            setSelectedClassificationTagId(cardUrlState.classificationTagId);
            currentClassificationTagIdRef.current = cardUrlState.classificationTagId;
        } else if (cardUrlState.knowledgeBookId) {
            const knowledgeBook = knowledgeBooks.find(item => item.ID === cardUrlState.knowledgeBookId);
            const classificationTagId = knowledgeBook?.ClassificationTags?.find(tagId => classificationTagIdSet.has(tagId));
            setSelectedClassificationTagId(classificationTagId);
            currentClassificationTagIdRef.current = classificationTagId;
        } else {
            setSelectedClassificationTagId(undefined);
            currentClassificationTagIdRef.current = undefined;
        }
        setRevealedKnowledgeBookId(isLegacyNotClassifiedUrl ? null : (cardUrlState.knowledgeBookId || null));
    }, [cardUrlState.classificationTagId, cardUrlState.isCardView, cardUrlState.knowledgeBookId, classificationTagIdSet, knowledgeBooks]);

    const getOntologyAndDescendantIds = useCallback((ontologyId: string): Set<string> => {
        const applicableIds = new Set<string>();
        const stack = [ontologyId];

        while (stack.length > 0) {
            const currentId = stack.pop();
            if (!currentId || applicableIds.has(currentId)) {
                continue;
            }

            applicableIds.add(currentId);
            const childIds = ontologyHierarchy[currentId]?.childIds || [];
            childIds.forEach(childId => stack.push(childId));
        }

        return applicableIds;
    }, [ontologyHierarchy]);

    const filteredKnowledgeBooks = useMemo(() => {
        const applicableOntologyIds = selectedOntologyId ? getOntologyAndDescendantIds(selectedOntologyId) : null;

        return filterKnowledgeBooksBySearchText({
            knowledgeBooks: knowledgeBooks
            .filter(kb => !showFavoritesOnly || favoriteKnowledgeBookIdSet.has(kb.ID))
            .filter(kb => !applicableOntologyIds || (!!kb.OntologyID && applicableOntologyIds.has(kb.OntologyID)))
            .filter(kb => canViewKnowledgeBook(kb))
            .filter(kb => {
                if (!effectiveClassificationFilterTagIds) {
                    return true;
                }

                const tagIds = kb.ClassificationTags || [];
                if (tagIds.length === 0) {
                    return false;
                }

                return tagIds.some(tagId => effectiveClassificationFilterTagIds.has(tagId));
            })
            ,
            classificationTags,
            searchText
        })
            .sort((left, right) => classificationFilterSortDirection === 'asc'
                ? left.Name.localeCompare(right.Name)
                : right.Name.localeCompare(left.Name));
    }, [canViewKnowledgeBook, classificationFilterSortDirection, classificationTags, effectiveClassificationFilterTagIds, favoriteKnowledgeBookIdSet, getOntologyAndDescendantIds, knowledgeBooks, searchText, selectedOntologyId, showFavoritesOnly]);

    const browseModel = useMemo(() => {
        return buildKnowledgeLibraryBrowseModel({
            knowledgeBooks: filteredKnowledgeBooks,
            classificationTags,
            selectedTagId: selectedClassificationTagId,
            allowedClassificationTagIds: effectiveClassificationFilterTagIds,
            classificationSortDirection: classificationFilterSortDirection,
            labels: {
                library: InovxLoader.t('facility:knowledgeBook.library'),
                emptyRoot: InovxLoader.t('facility:knowledgeBook.noMatchingKnowledgeBooks'),
                emptyTaxonomy: InovxLoader.t('facility:knowledgeBook.noKnowledgeBooksInClassification')
            }
        });
    }, [classificationFilterSortDirection, classificationTags, effectiveClassificationFilterTagIds, filteredKnowledgeBooks, selectedClassificationTagId]);

    const visibleKnowledgeBookIdSet = useMemo(() => {
        if (viewMode === 'card') {
            return new Set(browseModel.knowledgeBookCards.map(card => card.id));
        }

        return new Set(filteredKnowledgeBooks.map(knowledgeBook => knowledgeBook.ID));
    }, [browseModel.knowledgeBookCards, filteredKnowledgeBooks, viewMode]);

    const knowledgeLibraryHierarchyKey = useMemo(() => {
        return taxonomyDetail?.VersionToken || 'ready';
    }, [taxonomyDetail?.VersionToken]);

    useEffect(() => {
        setSelectedKnowledgeBookIdSet(previous => {
            let changed = false;
            const next = new Set<string>();

            previous.forEach(id => {
                if (visibleKnowledgeBookIdSet.has(id)) {
                    next.add(id);
                } else {
                    changed = true;
                }
            });

            return changed ? next : previous;
        });
    }, [visibleKnowledgeBookIdSet]);

    const selectedKnowledgeBooks = useMemo(() => {
        if (selectedKnowledgeBookIdSet.size === 0) {
            return [];
        }

        return knowledgeBooks.filter(knowledgeBook => selectedKnowledgeBookIdSet.has(knowledgeBook.ID));
    }, [knowledgeBooks, selectedKnowledgeBookIdSet]);

    const selectedCount = selectedKnowledgeBookIdSet.size;
    const allSelectedAreFavorites = useMemo(() => {
        if (selectedCount === 0) return false;
        return selectedKnowledgeBooks.every(kb => favoriteKnowledgeBookIdSet.has(kb.ID));
    }, [favoriteKnowledgeBookIdSet, selectedCount, selectedKnowledgeBooks]);

    const noneSelectedAreFavorites = useMemo(() => {
        if (selectedCount === 0) return true;
        return selectedKnowledgeBooks.every(kb => !favoriteKnowledgeBookIdSet.has(kb.ID));
    }, [favoriteKnowledgeBookIdSet, selectedCount, selectedKnowledgeBooks]);

    const canModifySelectedKnowledgeBooks = useMemo(() => {
        return selectedKnowledgeBooks.length > 0 && selectedKnowledgeBooks.every(knowledgeBook => ComponentHelpers.hasModifyKnowledgeBookPermission(knowledgeBook));
    }, [selectedKnowledgeBooks]);

    const canManageSelectedKnowledgeBooks = useMemo(() => {
        return selectedKnowledgeBooks.length > 0 && selectedKnowledgeBooks.every(knowledgeBook => ComponentHelpers.hasManageKnowledgeBookPermission(knowledgeBook));
    }, [selectedKnowledgeBooks]);

    const handleAssignSelectedTags = useCallback(() => {
        if (selectedKnowledgeBooks.length === 0) return;
        if (!selectedKnowledgeBooks.every(canModifyKnowledgeBook)) {
            SystemActions.displayInfoMessage({ message: InovxLoader.t('facility:message.selectedKnowledgeBooksViewOnly') });
            return;
        }

        handleAssignTags(selectedKnowledgeBooks);
    }, [canModifyKnowledgeBook, handleAssignTags, selectedKnowledgeBooks]);

    const handleAddSelectedToFavorites = useCallback(() => {
        if (selectedKnowledgeBooks.length === 0) return;
        handleAddToFavorites(selectedKnowledgeBooks);
    }, [handleAddToFavorites, selectedKnowledgeBooks]);

    const handleRemoveSelectedFromFavorites = useCallback(() => {
        if (selectedKnowledgeBooks.length === 0) return;
        handleRemoveFromFavorites(selectedKnowledgeBooks);
    }, [handleRemoveFromFavorites, selectedKnowledgeBooks]);

    const handleDeleteSelectedKnowledgeBooks = useCallback(() => {
        if (selectedKnowledgeBooks.length === 0) return;
        if (!selectedKnowledgeBooks.every(canManageKnowledgeBookByTags)) {
            SystemActions.displayInfoMessage({ message: InovxLoader.t('facility:message.selectedKnowledgeBooksNotManageable') });
            return;
        }

        handleDeleteKnowledgeBooks(selectedKnowledgeBooks);
    }, [canManageKnowledgeBookByTags, handleDeleteKnowledgeBooks, selectedKnowledgeBooks]);

    const selectionToolbar = useMemo(() => (
        <KnowledgeLibraryBulkSelectionToolbar
            key={selectedCount}
            selectedCount={selectedCount}
            allSelectedAreFavorites={allSelectedAreFavorites}
            noneSelectedAreFavorites={noneSelectedAreFavorites}
            canModifySelectedKnowledgeBooks={canModifySelectedKnowledgeBooks}
            canManageSelectedKnowledgeBooks={canManageSelectedKnowledgeBooks}
            onClearSelection={clearKnowledgeBookSelection}
            onAssignTags={handleAssignSelectedTags}
            onAddToFavorites={handleAddSelectedToFavorites}
            onRemoveFromFavorites={handleRemoveSelectedFromFavorites}
            onDeleteKnowledgeBooks={handleDeleteSelectedKnowledgeBooks}
        />
    ), [allSelectedAreFavorites, canManageSelectedKnowledgeBooks, canModifySelectedKnowledgeBooks, clearKnowledgeBookSelection, handleAddSelectedToFavorites, handleAssignSelectedTags, handleDeleteSelectedKnowledgeBooks, handleRemoveSelectedFromFavorites, noneSelectedAreFavorites, selectedCount]);

    const handleFavoritesFilterToggle = useCallback(() => {
        setShowFavoritesOnly(previous => !previous);
    }, []);

    const handleSelectTag = useCallback((tagId: string) => {
        currentClassificationTagIdRef.current = tagId;
        setSelectedClassificationTagId(tagId);
        setRevealedKnowledgeBookId(null);
        FacilityActions.setKnowledgeLibraryCardUrlState({ isCardView: true, classificationTagId: tagId, knowledgeBookId: undefined });
    }, []);

    const handleBreadcrumbClick = useCallback((breadcrumb: KnowledgeLibraryBreadcrumb) => {
        if (breadcrumb.kind === 'root') {
            currentClassificationTagIdRef.current = undefined;
            setSelectedClassificationTagId(undefined);
            setRevealedKnowledgeBookId(null);
            FacilityActions.setKnowledgeLibraryCardUrlState({ isCardView: true, classificationTagId: undefined, knowledgeBookId: undefined });
            return;
        }

        if (breadcrumb.kind === 'tag') {
            currentClassificationTagIdRef.current = breadcrumb.tagId;
            setSelectedClassificationTagId(breadcrumb.tagId);
            setRevealedKnowledgeBookId(null);
            FacilityActions.setKnowledgeLibraryCardUrlState({ isCardView: true, classificationTagId: breadcrumb.tagId, knowledgeBookId: undefined });
        }
    }, []);

    const getCurrentClassificationTagIdForUrl = useCallback(() => {
        return currentClassificationTagIdRef.current;
    }, []);

    const handleViewModeChange = useCallback((nextViewMode: KnowledgeLibraryViewMode) => {
        const currentClassificationTagId = getCurrentClassificationTagIdForUrl();
        detailKnowledgeBookRequestRef.current += 1;
        setDetailKnowledgeBook(null);
        setDetailKnowledgeViews(current => current.length === 0 ? current : []);
        setIsLoadingDetailKnowledgeViews(false);
        setRevealedKnowledgeBookId(null);
        setViewMode(nextViewMode);
        FacilityActions.setKnowledgeLibraryCardUrlState({
            isCardView: nextViewMode === 'card',
            classificationTagId: nextViewMode === 'card' ? currentClassificationTagId : undefined,
            knowledgeBookId: undefined
        });
    }, [getCurrentClassificationTagIdForUrl]);

    const handleRevealedKnowledgeBookIdChange = useCallback((knowledgeBookId: string | null) => {
        const currentClassificationTagId = getCurrentClassificationTagIdForUrl();
        setRevealedKnowledgeBookId(knowledgeBookId);
        FacilityActions.setKnowledgeLibraryCardUrlState({
            isCardView: true,
            classificationTagId: currentClassificationTagId,
            knowledgeBookId: knowledgeBookId || undefined
        });
    }, [getCurrentClassificationTagIdForUrl]);

    const handleKnowledgeViewsDrawerClose = useCallback(() => {
        detailKnowledgeBookRequestRef.current += 1;
        setDetailKnowledgeBook(null);
        setDetailKnowledgeViews(current => current.length === 0 ? current : []);
        setIsLoadingDetailKnowledgeViews(false);
        setRevealedKnowledgeBookId(null);
        FacilityActions.setKnowledgeLibraryCardUrlState({
            isCardView: viewMode === 'card',
            classificationTagId: viewMode === 'card' ? getCurrentClassificationTagIdForUrl() : undefined,
            knowledgeBookId: undefined
        });
    }, [getCurrentClassificationTagIdForUrl, viewMode]);

    const handlePreviewKnowledgeBook = useCallback(async (kb: IKnowledgeBook) => {
        if (!canViewKnowledgeBook(kb)) {
            SystemActions.displayInfoMessage({ message: InovxLoader.t('facility:message.knowledgeBookNoViewPermission') });
            return;
        }

        const currentViewMode = viewModeRef.current;
        const currentClassificationTagId = getCurrentClassificationTagIdForUrl();

        if (currentViewMode === 'card') {
            setRevealedKnowledgeBookId(kb.ID);
            FacilityActions.setKnowledgeLibraryCardUrlState({
                isCardView: true,
                classificationTagId: currentClassificationTagId,
                knowledgeBookId: kb.ID
            });
        }

        const requestId = detailKnowledgeBookRequestRef.current + 1;
        detailKnowledgeBookRequestRef.current = requestId;
        setDetailKnowledgeBook(kb);
        setDetailKnowledgeViews(current => current.length === 0 ? current : []);
        setIsLoadingDetailKnowledgeViews(true);

        try {
            const knowledgeViews = await KnowledgeViewServices.getKnowledgeViews(kb.ID);
            if (mountedRef.current && detailKnowledgeBookRequestRef.current === requestId) {
                setDetailKnowledgeViews(knowledgeViews || []);
            }
        } finally {
            if (mountedRef.current && detailKnowledgeBookRequestRef.current === requestId) {
                setIsLoadingDetailKnowledgeViews(false);
            }
        }
    }, [canViewKnowledgeBook, getCurrentClassificationTagIdForUrl]);

    return (
        <div className="kl-main-container d-flex j-cc" id="kb-list">
            <div className="kv-container" ref={knowledgeViewsContainerRef}>
                <OperationProgressDialog visible={!!operationMessage} message={operationMessage || undefined} />
                <div className="d-flex j-csb my-3 knowledge-library-heading-row">
                    <div className="knowledge-library-heading-actions">
                        <div className="page-title">{InovxLoader.t('facility:menu.knowledgeLibrary')}</div>
                        {selectedFacility && ComponentHelpers.hasAnyModifyKnowledgeBookPermission() && (
                            <button
                                type="button"
                                className="btn btn-primary knowledge-library-new-book-button"
                                onClick={() => setIsNewKnowledgeBookDialogOpen(true)}
                            >
                                {InovxLoader.t('facility:assetSetVisualization.newKnowledgeBook')}
                            </button>
                        )}
                    </div>
                    <div className="knowledge-library-view-toggle" role="group" aria-label={InovxLoader.t('facility:knowledgeBook.layout')}>
                        <button
                            type="button"
                            className={viewMode === 'card' ? 'e-btn e-flat kb-filter-toggle active knowledge-library-layout-toggle' : 'e-btn e-flat kb-filter-toggle knowledge-library-layout-toggle'}
                            title={InovxLoader.t('facility:knowledgeBook.cardView')}
                            onClick={() => handleViewModeChange('card')}
                        >
                            <span className="toggle-icon card-view"></span>
                        </button>
                        <button
                            type="button"
                            className={viewMode === 'list' ? 'e-btn e-flat kb-filter-toggle active knowledge-library-layout-toggle' : 'e-btn e-flat kb-filter-toggle knowledge-library-layout-toggle'}
                            title={InovxLoader.t('facility:knowledgeBook.listView')}
                            onClick={() => handleViewModeChange('list')}
                        >
                            <span className="toggle-icon list-view"></span>
                        </button>
                    </div>
                </div>
                <Inovx.Shared.Core.ReactLoader appReady={true}>
                    <div className="kb-library-app-content">
                        <KnowledgeLibraryFilterBar
                            facilityHierarchy={facilityHierarchy}
                            selectedOntologyId={selectedOntologyId}
                            onOntologyChange={setSelectedOntologyId}
                            showFavoritesOnly={showFavoritesOnly}
                            onFavoritesToggle={handleFavoritesFilterToggle}
                            searchText={searchText}
                            onSearchTextChange={setSearchText}
                            showClassificationFilter={true}
                            classificationFilterValues={classificationFilterValues}
                            selectedClassificationFilterIds={classificationFilterTagIds}
                            onClassificationFilterChange={setClassificationFilterTagIds}
                            classificationFilterSortDirection={classificationFilterSortDirection}
                            onClassificationFilterSortDirectionChange={setClassificationFilterSortDirection}
                        />

                        {viewMode === 'card' ? (
                            <KnowledgeLibraryCards
                                browseModel={browseModel}
                                detailKnowledgeBookId={detailKnowledgeBook?.ID}
                                detailKnowledgeViews={detailKnowledgeViews}
                                isLoadingDetailKnowledgeViews={isLoadingDetailKnowledgeViews}
                                favoriteKnowledgeBookIds={favoriteKnowledgeBookIds}
                                selectedKnowledgeBookIdSet={selectedKnowledgeBookIdSet}
                                selectionToolbar={selectionToolbar}
                                onSelectTag={handleSelectTag}
                                onBreadcrumbClick={handleBreadcrumbClick}
                                onOpenKnowledgeBook={openKnowledgeBookFromLibrary}
                                onToggleFavorite={handleFavoriteClick}
                                onToggleKnowledgeBookSelection={handleToggleKnowledgeBookSelection}
                                revealedKnowledgeBookId={revealedKnowledgeBookId}
                                onRevealedKnowledgeBookIdChange={handleRevealedKnowledgeBookIdChange}
                                onRevealedKnowledgeBookChange={handleRevealedKnowledgeBookChange}
                                onBeginEdit={handleBeginEdit}
                                canModifyKnowledgeBook={canModifyKnowledgeBook}
                                canManageKnowledgeBook={canManageKnowledgeBookByTags}
                            />
                        ) : (
                            <KnowledgeLibraryHierarchy
                                key={`knowledge-library-hierarchy-${knowledgeLibraryHierarchyKey}`}
                                knowledgeBooks={filteredKnowledgeBooks}
                                favoriteKnowledgeBookIds={favoriteKnowledgeBookIds}
                                selectedKnowledgeBookIdSet={selectedKnowledgeBookIdSet}
                                classificationFilterTagIds={effectiveClassificationFilterTagIds}
                                classificationSortDirection={classificationFilterSortDirection}
                                selectedFacility={selectedFacility}
                                onBeginEdit={handleBeginEdit}
                                onCommandClick={handleCommandClick}
                                onPreviewKnowledgeBook={handlePreviewKnowledgeBook}
                                onFavoriteClick={handleFavoriteClick}
                                onToggleKnowledgeBookSelection={handleToggleKnowledgeBookSelection}
                                onClearSelection={clearKnowledgeBookSelection}
                                onAssignSelectedTags={handleAssignSelectedTags}
                                onAddSelectedToFavorites={handleAddSelectedToFavorites}
                                onRemoveSelectedFromFavorites={handleRemoveSelectedFromFavorites}
                                onDeleteSelectedKnowledgeBooks={handleDeleteSelectedKnowledgeBooks}
                                canModifyKnowledgeBook={canModifyKnowledgeBook}
                                canManageKnowledgeBook={canManageKnowledgeBookByTags}
                            />
                        )}

                        {isModalOpen && <KnowledgeBookForm />}
                        <KnowledgeBookAssignClassificationTagsForm />
                    </div>
                </Inovx.Shared.Core.ReactLoader>
            </div>
            <KnowledgeViewsDrawer
                isOpen={!!detailKnowledgeBook}
                knowledgeBookName={detailKnowledgeBook?.Name || ''}
                knowledgeViews={detailKnowledgeViews}
                isLoading={isLoadingDetailKnowledgeViews}
                boundsRef={knowledgeViewsContainerRef}
                onOpenKnowledgeView={openKnowledgeViewFromLibrary}
                onClose={handleKnowledgeViewsDrawerClose}
                isSidebarOpen={state.isSidebarOpen}
            />
            {isNewKnowledgeBookDialogOpen && state.selectedFacility && (
                <NewKnowledgeBookDialog
                    initialClassificationTagId={selectedClassificationTagId}
                    facility={state.selectedFacility}
                    onClosed={() => setIsNewKnowledgeBookDialogOpen(false)}
                    onCreated={handleNewKnowledgeBookCreated}
                />
            )}
        </div>
    );
};

export default KnowledgeLibrary;
